<script>
    import moment from '../../modules/moment';
    import get from 'lodash/get';

    export default {
        name: 'space-validation',

        computed: {
            momentStartDate() {
                return new moment(`${ this.reservationData.start_date } ${ this.reservationData.start_time }`, 'DD-MM-YYYY HH:mm');
            },

            momentEndDate() {
                return new moment(`${ this.reservationData.end_date } ${ this.reservationData.end_time }`, 'DD-MM-YYYY HH:mm');
            },

            disabledTimes() {
                if (!this.availability || !this.availability.unavailable_access_times.length) {
                    return [];
                }

                const unavailableTimes = this.availability.unavailable_access_times.filter((time) => {
                    const thisDate = new moment(this.end_date, 'DD-MM-YYYY').format('YYYY-MM-DD');

                    return time.includes(thisDate);
                }).map((time) => moment(time).isDST() ? moment(time).format('HH:mm') : moment.utc(time).format('HH:mm'));

                if (unavailableTimes.find((time) => time === '23:30')) {
                    unavailableTimes.push('23:45');
                }

                return unavailableTimes.map((time) => time.split(':').map((t) => parseInt(t)));
            },

            startTimeConfig() {
                return {
                    interval: 15,
                    min: this.minStartTime(this.start_date),
                    disable: this.disabledTimes,
                };
            },

            endTimeConfig() {
                return {
                    interval: 15,
                    min: this.minEndTime(this.start_date, this.start_time, this.end_date),
                    disable: this.disabledTimes,
                };
            },

            /**
             * returns an element which we want to attach the start-date calendar to,
             * or a function which returns such element
             */
            startDateContainer() {
                if ($(window).width() >= 768) {
                    return false;
                }

                return this.$refs.startDateContainer;
            },

            /**
             * returns an element which we want to attach the end-date calendar to,
             * or a function which returns such element
             */
            endDateContainer() {
                if ($(window).width() >= 768) {
                    return false;
                }

                return this.$refs.endDateContainer;
            },

            startDateConfig() {
                const $vm = this;

                return {
                    disable: [
                        function(date) {
                            if (!$vm.availability) {
                                return [];
                            }

                            let unavailable = ['fully_booked', 'unavailable'].reduce((unavailable, item) => {
                                return unavailable.concat($vm.availability[item]);
                            }, []);

                            /**
                             * If the format is the the array of time strings we need to format them using moment to 'YYYY-MM-DD' so that our datepicker can interpret them correctly.
                             */
                            unavailable = unavailable.map((date) => {
                                return new moment(date).format('YYYY-MM-DD');
                            });

                            return unavailable.includes(new moment(date).format('YYYY-MM-DD'));
                        },
                    ],

                    appendTo: this.startDateContainer,
                    minDate: this.minStartDate(),
                    maxDate: this.maxStartDate(),
                    onDayCreate: (dObj, dStr, fp, dayElem) => {
                        this.addDayAvailabilityStatuses(dayElem);
                    },
                };
            },

            endDateConfig() {
                return {
                    minDate: this.minEndDate(this.start_date),
                    appendTo: this.endDateContainer,
                    onDayCreate: (dObj, dStr, fp, dayElem) => {
                        this.addDayAvailabilityStatuses(dayElem);

                        const startDate = this.start_date;
                        const endDate = this.end_date;

                        if (
                            moment(dayElem.dateObj).isAfter(moment(startDate, 'DD-MM-YYYY'))
                            && moment(dayElem.dateObj).isBefore(moment(endDate, 'DD-MM-YYYY'))
                        ) {
                            dayElem.classList.add('selected-range');
                        }

                        if (moment(dayElem.dateObj).format('DD-MM-YYYY') === startDate) {
                            dayElem.classList.add('start-date');
                        }

                        if (moment(dayElem.dateObj).format('DD-MM-YYYY') === endDate) {
                            dayElem.classList.add('end-date');
                        }
                    },
                    maxDate: this.maxEndDate(),
                };
            },
        },

        mounted() {
            // Update the local storage if our reservation details change
            ['start_date', 'end_date', 'start_time', 'end_time', 'rental', 'interval_count'].forEach((item) => {
                this.$watch(item, () => {
                    try {
                        window.localStorage.setItem(`search-${ item }`, this[item]);
                    } catch (e) {
                        // TODO: find out better way to store this
                    }
                });
            });
        },

        methods: {
            /**
             * Constrain the maximum start date
             *
             * @returns {string|null}
             */
            maxStartDate() {
                const upperPeriodLimit = get(this, '$store.state.booking.space.upper_period_limit') || get(this, 'space.upper_period_limit');
                return upperPeriodLimit ? moment().add(upperPeriodLimit, 'd').format('DD-MM-YYYY') : null;
            },

            /**
             * Constrain the end date
             *
             * @returns {*}
             */
            maxEndDate() {
                const fullyBooked = this.availability.fully_booked.days;
                const unavailable = this.availability.unavailable.days;
                let disabledDates = [];

                if (unavailable && fullyBooked) {
                    disabledDates = unavailable.concat(fullyBooked);
                }

                disabledDates = disabledDates
                    .filter((date) => moment(date).isAfter(moment(this.start_date, 'DD-MM-YYYY')))
                    .map((date) => moment(date));

                if (disabledDates.length) {
                    return moment.min(disabledDates).format('DD-MM-YYYY');
                }

                if (this.$store.state.booking.space.overnight === false) {
                    return this.start_date;
                }

                return null;
            },

            /**
             * Add the colour coded statuses to the days in the calendar
             *
             * @param {HTMLElement} dayElem
             */
            addDayAvailabilityStatuses(dayElem) {
                let fullyBooked = [];
                let unavailable = [];
                let lowAvailability = [];

                if (this.availability) {
                    if (this.availability.fully_booked.days.length) {
                        fullyBooked = this.availability.fully_booked.days;
                    }

                    if (this.availability.unavailable.length) {
                        unavailable = this.availability.unavailable
                            .map((date) => moment(date).format('YYYY-MM-DD'));
                    }

                    if (this.availability.low_availability.length) {
                        lowAvailability = this.availability.low_availability;
                    }
                }

                if (this.rental === 'short') {
                    if (
                        fullyBooked.includes(moment(dayElem.dateObj).format('YYYY-MM-DD'))
                        || unavailable.includes(moment(dayElem.dateObj).format('YYYY-MM-DD'))
                    ) {
                        dayElem.classList.add('unavailable', 'disabled');
                        dayElem.title = 'Unavailable';
                        dayElem.setAttribute('data-toggle', 'tooltip');
                    } else if (lowAvailability.includes(moment(dayElem.dateObj).format('YYYY-MM-DD'))) {
                        dayElem.classList.add('low-availability');
                        dayElem.title = 'Low Availability';
                        dayElem.setAttribute('data-toggle', 'tooltip');
                    }
                } else if (unavailable.includes(moment(dayElem.dateObj).format('YYYY-MM-DD'))) {
                    dayElem.classList.add('unavailable', 'disabled');
                    dayElem.title = 'Unavailable';
                    dayElem.setAttribute('data-toggle', 'tooltip');
                }

                if (
                    !dayElem.classList.contains('disabled')
                    && !dayElem.classList.contains('nextMonthDay')
                    && !dayElem.classList.contains('prevMonthDay')
                    && !dayElem.classList.contains('low-availability')
                ) {
                    dayElem.classList.add('available');
                    dayElem.title = 'Available';
                    dayElem.setAttribute('data-toggle', 'tooltip');
                }
            },
        },
    };
</script>
